import {
  Direction,
  Insight,
  SupportedDxObjectType,
  TrendDetail,
  TrendGroupDetail,
} from '@fullstory/proto/fs/api/proactiveinsights';
import { int64 } from '@fullstory/proto/google/protobuf';
import {
  AppState,
  InsightDetail,
  InsightTrendDetail,
  InsightTrendGroupByDetail,
  InsightTrendGroupByWords,
  InsightTrendWords,
  InsightsById,
  getInsightTitle,
  getInsightWords,
} from '@fullstory/redux';
import { hashObject } from '@fullstory/util/hash';
import { cloneDeep } from 'lodash';
import { DeepPartial } from '@fullstory/typeutils';
import {
  MOCK_AVG_MONTHLY_INSIGHT,
  MOCK_AVG_WEEKLY_INSIGHT,
  MOCK_TREND_GROUP_BY,
  MOCK_VOL_MONTHLY_INSIGHT,
  MOCK_VOL_WEEKLY_INSIGHT,
} from './responses';

export function getMockInsight(
  isMonthly: boolean,
  isVolatilityTrend: boolean,
  ratioChange: number,
): InsightDetail {
  const insight = {
    ...cloneDeep(getInsight(isMonthly, isVolatilityTrend)),
  };
  if (insight.trend) {
    insight.trend.ratioChange = ratioChange;
  }
  const insightWords = getInsightWords(insight);
  return {
    ...insight,
    insightWords,
    dxIdObjectHash: 123,
    title: getInsightTitle(insightWords, insight),
  };
}

const handleDxIdVersion = (insight: InsightDetail, id?: string, version?: number) => {
  if (insight?.dxObject && version !== undefined) {
    insight.dxObject.version = int64.fromNumber(version);
  }

  if (insight?.dxObject && id !== undefined) {
    insight.dxObject.id = id;
  }

  if (insight?.dxIdObjectHash && id !== undefined && version !== undefined) {
    insight.dxIdObjectHash = hashObject({
      id,
      version: version.toString(),
      supportedDxObjectType: SupportedDxObjectType.SUPPORTED_DX_OBJECT_TYPE_METRIC,
    });
  }
};

const getInsight = (isMonthly: boolean, isVolatilityTrend: boolean) => {
  if (isMonthly) {
    return isVolatilityTrend ? MOCK_VOL_MONTHLY_INSIGHT : MOCK_AVG_MONTHLY_INSIGHT;
  } else {
    return isVolatilityTrend ? MOCK_VOL_WEEKLY_INSIGHT : MOCK_AVG_WEEKLY_INSIGHT;
  }
};

export function getMockInsightFromResponse(insight: Insight): InsightTrendDetail {
  const insightWords = getInsightWords(insight);
  const clonedInsight = cloneDeep(insight);
  const trend: TrendDetail = clonedInsight.trend ?? {
    data: null,
    direction: Direction.DIRECTION_INVALID,
    periodicStats: [],
    ratioChange: 0,
    trendSteps: null,
  };
  return {
    ...clonedInsight,
    trend,
    insightWords: insightWords as InsightTrendWords,
    dxIdObjectHash: hashObject(insight.dxObject),
    title: getInsightTitle(insightWords, insight),
  };
}

export function getMockAverageInsight(
  mean?: number,
  version?: number,
  id?: string,
): InsightTrendDetail {
  const mockedInsight = getMockInsightFromResponse(MOCK_AVG_WEEKLY_INSIGHT);
  const periodicStats = mockedInsight.trend.periodicStats;

  if (periodicStats.length > 0 && mean) {
    const lastEntry = periodicStats[periodicStats.length - 1];
    if (lastEntry && lastEntry.mean) {
      lastEntry.mean = mean;
      if (mockedInsight.trend?.periodicStats[periodicStats.length - 1]) {
        mockedInsight.trend.periodicStats[periodicStats.length - 1] = lastEntry;
      }
    }
  }
  handleDxIdVersion(mockedInsight, id, version);
  return mockedInsight;
}

export function getMockVolInsight(
  lastStdUpper?: number,
  lastStdLower?: number,
  version?: number,
  id?: string,
): InsightTrendDetail {
  const mockedInsight = getMockInsightFromResponse(MOCK_VOL_WEEKLY_INSIGHT);
  const periodicStats = mockedInsight.trend.periodicStats;
  if (lastStdUpper && lastStdLower && periodicStats.length > 0) {
    const lastEntry = periodicStats[periodicStats.length - 1];
    if (lastEntry && lastEntry.variability) {
      lastEntry.variability.stdLower = lastStdLower;
      lastEntry.variability.stdUpper = lastStdUpper;
      if (mockedInsight.trend?.periodicStats[periodicStats.length - 1]) {
        mockedInsight.trend.periodicStats[periodicStats.length - 1] = lastEntry;
      }
    }
  }

  handleDxIdVersion(mockedInsight, id, version);
  return mockedInsight;
}

export const getMockBoundingRectangle = () => ({
  width: 1200,
  height: 1200,
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
  x: 0,
  y: 0,
  toJSON: () => {},
});

// Trend Group By Insights have 4 possible states:
// top-value-unchanged: Insight has a singleton top group that is unchanged across all weeks
// top-value-changed: Insight has a singleton top group that is not same across all weeks
// top-tier-unchanged: Insight has more than one group in top tier (2 or 3), and the top tier is unchanged across all weeks.
// top-tier-changed: Insight has more than one group in top tier (2 or 3), and the top tier is not same across all weeks.
// In addition to these states, the trend group by legend/tooltip must support displaying named elements, and page definitions
// which are available on the 'named-element' and 'page-def' type.
export type MockTrendGroupByInsightTypes =
  | 'top-value-unchanged'
  | 'top-value-changed'
  | 'top-tier-unchanged'
  | 'top-tier-changed'
  | 'named-element'
  | 'page-defs';

interface MockTrendGroupConfig {
  type?: MockTrendGroupByInsightTypes;
  version?: number;
  topValueChange?: number;
  gapValueChange?: number;
  groupIds?: string[];
}

export function getMockTrendGroupByInsight(
  config?: MockTrendGroupConfig,
): InsightTrendGroupByDetail {
  const mockInsight: Insight = cloneDeep(MOCK_TREND_GROUP_BY);

  const trendGroup: TrendGroupDetail = mockInsight.trendGroup ?? {
    periodicTables: [],
    topValueChange: null,
    gapChange: null,
    featuresDetected: 0,
  };

  if (config !== undefined) {
    const { version, topValueChange, gapValueChange, type, groupIds } = config;
    if (mockInsight?.dxObject && version) {
      mockInsight.dxObject.version = int64.fromNumber(version);
    }

    if (topValueChange && trendGroup.topValueChange?.ratioChange) {
      trendGroup.topValueChange.ratioChange = topValueChange;
    }
    if (gapValueChange && trendGroup.gapChange?.ratioChange) {
      trendGroup.gapChange.ratioChange = gapValueChange;
    }
    if (type) {
      applyInsightType(trendGroup, type);
    }
    if (groupIds) {
      trendGroup.periodicTables = trendGroup.periodicTables.map(pt => {
        const dataRows = pt.aggregateTable?.metricsDataTable?.table ?? [];
        return {
          ...pt,
          dataRows: dataRows.map((d, i) => {
            d.groups = [{ groupId: groupIds[i] ?? '', fallback: false }];
            return d;
          }),
        };
      });
    }
  }

  const insightWords = getInsightWords(mockInsight) as InsightTrendGroupByWords;
  return {
    ...mockInsight,
    trendGroup,
    insightWords,
    dxIdObjectHash: 123,
    title: getInsightTitle(insightWords, mockInsight),
  };
}

const applyInsightType = (trendGroup: TrendGroupDetail, type: MockTrendGroupByInsightTypes) => {
  switch (type) {
    case 'top-tier-changed': {
      trendGroup.periodicTables.map((pt, i) => {
        const dataRows = pt.aggregateTable?.metricsDataTable?.table ?? [];
        if (dataRows.length == 0 || !dataRows[0]?.groups || !dataRows[1]?.groups) {
          return;
        }
        pt.kneeIndex = 2;
        const topTierGroupNames =
          i === trendGroup.periodicTables.length - 1 ? ['Firefox', 'Brave'] : ['Brave', 'Edge'];
        dataRows[0].groups = [{ groupId: topTierGroupNames[0] ?? '', fallback: false }];
        dataRows[1].groups = [{ groupId: topTierGroupNames[1] ?? '', fallback: false }];
      });
      break;
    }
    case 'top-tier-unchanged': {
      trendGroup.periodicTables.map((pt, i) => {
        const dataRows = pt.aggregateTable?.metricsDataTable?.table ?? [];
        if (dataRows.length == 0 || !dataRows[0]?.groups || !dataRows[1]?.groups) {
          return;
        }
        pt.kneeIndex = 2;
        const topTierGroupNames =
          i === trendGroup.periodicTables.length - 1 ? ['Firefox', 'Brave'] : ['Brave', 'Firefox'];
        dataRows[0].groups = [{ groupId: topTierGroupNames[0] ?? '', fallback: false }];
        dataRows[1].groups = [{ groupId: topTierGroupNames[1] ?? '', fallback: false }];
      });
      break;
    }
    case 'top-value-changed': {
      trendGroup.periodicTables.map((pt, i) => {
        const dataRows = pt.aggregateTable?.metricsDataTable?.table ?? [];
        if (dataRows.length == 0 || !dataRows[0]?.groups) {
          return;
        }
        pt.kneeIndex = 1;
        const topTierGroupNames = i === trendGroup.periodicTables.length - 1 ? 'Firefox' : 'Brave';
        dataRows[0].groups = [{ groupId: topTierGroupNames[0] ?? '', fallback: false }];
      });
      break;
    }
    case 'top-value-unchanged': {
      trendGroup.periodicTables.map(pt => {
        const dataRows = pt.aggregateTable?.metricsDataTable?.table ?? [];
        if (dataRows.length == 0 || !dataRows[0]?.groups) {
          return;
        }
        pt.kneeIndex = 1;
        dataRows[0].groups = [{ groupId: 'Firefox', fallback: false }];
      });
      break;
    }
    case 'page-defs': {
      trendGroup.periodicTables.forEach(pt => {
        const dataRows = pt.aggregateTable?.metricsDataTable?.table ?? [];
        dataRows.forEach((dataRow, i) => {
          dataRow.groups = [{ groupId: i === dataRows.length - 1 ? '1' : '3', fallback: false }];
        });
      });
      break;
    }
    case 'named-element': {
      trendGroup.periodicTables.forEach(pt => {
        const dataRows = pt.aggregateTable?.metricsDataTable?.table ?? [];
        dataRows.forEach((dataRow, i) => {
          dataRow.groups = [
            {
              groupId: i === dataRows.length - 1 ? 'settings-tab' : 'search-button',
              fallback: false,
            },
          ];
        });
      });
      break;
    }
    default:
      break;
  }
};

export const getMockLabelGroups = () => [
  { groups: ['product-mgmt'], label: 'product-mgmt', id: '' },
  { groups: ['acct-mgmt'], label: 'acct-mgmt', id: '' },
  { groups: ['data'], label: 'data', id: '' },
  { groups: [''], label: 'Unknown', id: '' },
  { groups: ['engineering'], label: 'engineering', id: '' },
];

export const mockInitialMetricInsightsState = (
  insights: InsightDetail[],
): DeepPartial<AppState> => {
  const insightsByHash = insights.reduce<InsightsById>((acc, val) => {
    acc[val.insightId] = val;
    return acc;
  }, {});
  return {
    metricsInsights: {
      metricInsights: insightsByHash,
    },
  };
};
